// Simple in-memory cache with TTL for API responses.
// Caches are keyed by a string and scoped per organization.
// Cache is cleared on org switch.

const entries = new Map();
let currentOrgId = null;

function getCacheKey(key) {
    return `${currentOrgId}:${key}`;
}

export function setOrgScope(orgId) {
    if (orgId !== currentOrgId) {
        entries.clear();
        currentOrgId = orgId;
    }
}

/**
 * Get or fetch a cached value.
 * @param {string} key - Cache key
 * @param {Function} fetcher - Async function to call on cache miss
 * @param {number} ttlMs - Time-to-live in milliseconds (default 5 minutes)
 * @returns {Promise<any>} Cached or freshly fetched data
 */
export async function cached(key, fetcher, ttlMs = 5 * 60 * 1000) {
    const cacheKey = getCacheKey(key);
    const entry = entries.get(cacheKey);

    if (entry && Date.now() - entry.timestamp < ttlMs) {
        return entry.data;
    }

    // If there's already an in-flight request for this key, reuse it
    if (entry && entry.pending) {
        return entry.pending;
    }

    const pending = fetcher().then(data => {
        entries.set(cacheKey, { data, timestamp: Date.now(), pending: null });
        return data;
    }).catch(err => {
        // Remove failed entry so next call retries
        const current = entries.get(cacheKey);
        if (current && current.pending === pending) {
            entries.delete(cacheKey);
        }
        throw err;
    });

    entries.set(cacheKey, { ...(entry || {}), pending });
    return pending;
}

/**
 * Invalidate a specific cache key.
 */
export function invalidate(key) {
    entries.delete(getCacheKey(key));
}

/**
 * Invalidate all cache keys matching a prefix.
 */
export function invalidatePrefix(prefix) {
    const fullPrefix = getCacheKey(prefix);
    for (const key of entries.keys()) {
        if (key.startsWith(fullPrefix)) {
            entries.delete(key);
        }
    }
}

/**
 * Clear the entire cache.
 */
export function clearAll() {
    entries.clear();
}
